import { http } from './http';
import { unwrapData, unwrapList } from '../utils/unwrapApiData';

// ——— Action items (1–11) ———

export async function listActionItems(params = {}) {
  const { data } = await http.get('/action-items', { params });
  return unwrapList(data);
}

export async function listMyActionItems(params = {}) {
  const { data } = await http.get('/action-items/my', { params });
  return unwrapList(data);
}

export async function createActionItem(body) {
  const { data } = await http.post('/action-items', body);
  return unwrapData(data) ?? data;
}

export async function listActionItemsByMeeting(meetingId, params = {}) {
  const { data } = await http.get(`/action-items/meetings/${meetingId}`, { params });
  return unwrapList(data);
}

export async function getActionItem(id) {
  const { data } = await http.get(`/action-items/${id}`);
  return unwrapData(data) ?? data;
}

export async function updateActionItem(id, body) {
  const { data } = await http.put(`/action-items/${id}`, body);
  return unwrapData(data) ?? data;
}

export async function deleteActionItem(id) {
  await http.delete(`/action-items/${id}`);
}

export async function completeActionItem(id) {
  const { data } = await http.post(`/action-items/${id}/complete`);
  return unwrapData(data) ?? data;
}

export async function markActionItemInProgress(id) {
  const { data } = await http.post(`/action-items/${id}/in-progress`);
  return unwrapData(data) ?? data;
}

export async function syncActionItemToErp(id) {
  const { data } = await http.post(`/action-items/${id}/sync-to-erp`);
  return unwrapData(data) ?? data;
}

export async function listActionItemWebhookAttempts(id) {
  const { data } = await http.get(`/action-items/${id}/webhook-attempts`);
  return unwrapList(data);
}

export async function listActionPeople(params = {}) {
  const { data } = await http.get('/action-items/people', { params });
  return unwrapList(data);
}

export async function getActionItemFilterOptions(params = {}) {
  const { data } = await http.get('/action-items/filter-options', { params });
  return unwrapData(data) ?? data;
}

export async function getActionItemBoard(params = {}) {
  const { data } = await http.get('/action-items/board', { params });
  return unwrapData(data) ?? data;
}

export async function moveActionItem(id, body) {
  const { data } = await http.patch(`/action-items/${id}/stage`, body);
  return unwrapData(data) ?? data;
}

export async function listActionCollaborators(id) {
  const { data } = await http.get(`/action-items/${id}/collaborators`);
  return unwrapList(data);
}

export async function updateActionCollaborators(id, collaborators) {
  const { data } = await http.put(`/action-items/${id}/collaborators`, { collaborators });
  return unwrapData(data) ?? data;
}

export async function getActionWorkflow(params = {}) {
  const { data } = await http.get('/action-item-workflow/stages', { params });
  return unwrapData(data) ?? data;
}

export async function createActionStage(body) {
  const { data } = await http.post('/action-item-workflow/stages', body);
  return unwrapData(data) ?? data;
}

export async function updateActionStage(id, body) {
  const { data } = await http.patch(`/action-item-workflow/stages/${id}`, body);
  return unwrapData(data) ?? data;
}

export async function reorderActionStages(stageIds, params = {}) {
  const { data } = await http.patch('/action-item-workflow/stages/reorder', { stage_ids: stageIds, ...params });
  return unwrapData(data) ?? data;
}

export async function deleteActionStage(id, destinationStageId, params = {}) {
  const { data } = await http.delete(`/action-item-workflow/stages/${id}`, {
    data: { destination_stage_id: destinationStageId, ...params },
  });
  return unwrapData(data) ?? data;
}

export async function listActionChecklist(id) {
  const { data } = await http.get(`/action-items/${id}/checklist`);
  return unwrapList(data);
}

export async function createActionChecklistItem(id, body) {
  const { data } = await http.post(`/action-items/${id}/checklist`, body);
  return unwrapData(data) ?? data;
}

export async function updateActionChecklistItem(id, checkId, body) {
  const { data } = await http.patch(`/action-items/${id}/checklist/${checkId}`, body);
  return unwrapData(data) ?? data;
}

export async function deleteActionChecklistItem(id, checkId) {
  await http.delete(`/action-items/${id}/checklist/${checkId}`);
}

export async function listActionComments(id) {
  const { data } = await http.get(`/action-items/${id}/comments`);
  return unwrapList(data);
}

export async function createActionComment(id, body) {
  const { data } = await http.post(`/action-items/${id}/comments`, body);
  return unwrapData(data) ?? data;
}

export async function updateActionComment(id, commentId, body) {
  const { data } = await http.patch(`/action-items/${id}/comments/${commentId}`, body);
  return unwrapData(data) ?? data;
}

export async function deleteActionComment(id, commentId) {
  await http.delete(`/action-items/${id}/comments/${commentId}`);
}

export async function listActionActivity(id) {
  const { data } = await http.get(`/action-items/${id}/activity`);
  return unwrapList(data);
}

export async function listActionLabels(params = {}) {
  const { data } = await http.get('/action-items/labels', { params });
  return unwrapList(data);
}

export async function createActionLabel(body) {
  const { data } = await http.post('/action-items/labels', body);
  return unwrapData(data) ?? data;
}

export async function updateActionLabel(id, body) {
  const { data } = await http.patch(`/action-items/labels/${id}`, body);
  return unwrapData(data) ?? data;
}

export async function deleteActionLabel(id) {
  await http.delete(`/action-items/labels/${id}`);
}

export async function listItemLabels(id) {
  const { data } = await http.get(`/action-items/${id}/labels`);
  return unwrapList(data);
}

export async function syncItemLabels(id, labelIds) {
  const { data } = await http.put(`/action-items/${id}/labels`, { label_ids: labelIds });
  return unwrapData(data) ?? data;
}

export async function listActionAttachments(id) {
  const { data } = await http.get(`/action-items/${id}/attachments`);
  return unwrapList(data);
}

export async function addActionAttachment(id, body) {
  const { data } = await http.post(`/action-items/${id}/attachments`, body);
  return unwrapData(data) ?? data;
}

export async function deleteActionAttachment(id, attachmentId) {
  await http.delete(`/action-items/${id}/attachments/${attachmentId}`);
}

export async function listActionReminders(id) {
  const { data } = await http.get(`/action-items/${id}/reminders`);
  return unwrapList(data);
}

export async function createActionReminder(id, body) {
  const { data } = await http.post(`/action-items/${id}/reminders`, body);
  return unwrapData(data) ?? data;
}

export async function deleteActionReminder(id, reminderId) {
  await http.delete(`/action-items/${id}/reminders/${reminderId}`);
}

export async function claimActionReminderPopups() {
  const { data } = await http.post('/me/action-item-reminders/popup');
  return unwrapList(data);
}

export async function markActionReminderPopupShown(id, token) {
  const { data } = await http.post(`/me/action-item-reminders/${id}/popup-shown`, { token });
  return unwrapData(data) ?? data;
}
